import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Power, ShieldCheck } from 'lucide-react';
import { killSwitchApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label, Textarea } from '../components/ui/input';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { LoadingState, ErrorState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';

/**
 * Tenant-scoped kill switch control (BUI-960). Only ever reads/writes the caller's own
 * tenant — see apps/api/src/routes/kill-switch.ts for why platform scope is deliberately
 * not exposed here. `killSwitch:read` (admin+owner) gates seeing this page at all (nav
 * visibility is handled elsewhere); `killSwitch:manage` (owner only) gates the toggle.
 */
function ToggleForm({ mode, onDone }: { mode: 'enable' | 'disable'; onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => (mode === 'enable' ? killSwitchApi.enable(reason || undefined) : killSwitchApi.disable(reason || undefined)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kill-switch'] });
      toast({
        title: mode === 'enable' ? 'Kill switch enabled' : 'Kill switch disabled',
        description: mode === 'enable' ? 'All active and future scans for this workspace are now halted.' : 'Scans for this workspace may resume.',
        variant: mode === 'enable' ? 'error' : 'success',
      });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        mutation.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="kill-switch-reason">Reason (optional)</Label>
        <Textarea
          id="kill-switch-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={mode === 'enable' ? 'e.g. unexpected target behavior, customer request to pause' : 'e.g. issue resolved, safe to resume'}
        />
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" variant={mode === 'enable' ? 'danger' : 'primary'} className="w-full" disabled={mutation.isPending}>
        {mode === 'enable' ? 'Confirm: halt all scans' : 'Confirm: resume scans'}
      </Button>
    </form>
  );
}

export function KillSwitch() {
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'killSwitch:manage');
  const [dialogOpen, setDialogOpen] = useState(false);
  const query = useQuery({ queryKey: ['kill-switch'], queryFn: () => killSwitchApi.get() });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const state = query.data;
  const halted = state?.halted ?? false;
  const mode: 'enable' | 'disable' = halted ? 'disable' : 'enable';

  return (
    <div>
      <PageHeader title="Kill Switch" description="Immediate, workspace-wide emergency stop for all authorized scanning activity." />

      {halted ? (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-danger/40 bg-danger-soft p-4">
          <AlertTriangle size={22} className="mt-0.5 shrink-0 text-danger" />
          <div>
            <p className="font-semibold text-danger">Kill switch is ACTIVE — all scans halted</p>
            <p className="mt-1 text-sm text-ink-muted">
              No preflight check for this workspace will pass while this is active. Every in-flight and newly requested run is blocked.
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-success/40 bg-success-soft p-4">
          <ShieldCheck size={22} className="shrink-0 text-success" />
          <p className="font-semibold text-success">Kill switch is inactive — scanning proceeds normally</p>
        </div>
      )}

      <Card>
        <CardContent className="flex items-center justify-between gap-4 pt-6">
          <div className="space-y-1 text-sm">
            <p className="text-ink-muted">
              Last toggled by <span className="font-medium text-ink">{state?.by ?? 'nobody yet'}</span>
              {state?.at ? (
                <>
                  {' '}
                  at <span className="font-medium text-ink">{new Date(state.at).toLocaleString()}</span>
                </>
              ) : null}
            </p>
            {state?.reason ? <p className="text-ink-muted">Reason: {state.reason}</p> : null}
          </div>

          {canManage ? (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant={halted ? 'primary' : 'danger'}>
                  <Power size={16} />
                  {halted ? 'Disable kill switch' : 'Enable kill switch'}
                </Button>
              </DialogTrigger>
              <DialogContent
                title={halted ? 'Resume scanning for this workspace?' : 'Halt all scans for this workspace?'}
                description={
                  halted
                    ? 'This will allow preflight checks to pass again for this workspace. Any run that was blocked can now proceed.'
                    : 'This will immediately halt all active scans and block any new run from starting for this workspace. Confirm?'
                }
              >
                <ToggleForm mode={mode} onDone={() => setDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : (
            <p className="text-xs text-ink-muted">Only a workspace Owner can toggle the kill switch. You have read-only access.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
